import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { AppDispatch } from 'components/store/state/store'
import { fitBounds } from 'google-map-react'
import { updateCurrentClinique, updateCurrentStep, updateMapActiveMarker } from 'components/core/state/actions'
import { renderToString } from 'react-dom/server'
import { Wrapper } from '@googlemaps/react-wrapper'
import { IClinique, ISetSearchParams } from 'components/core/types'
import GoogleMap from 'components/maps/GoogleMap'
import GoogleMapInfoWindow from 'components/maps/GoogleMapInfoWindow'
import GoogleMapMarker from 'components/maps/GoogleMapMarker'
import DActiveMarker from 'assets/images/active-marker.svg'
import DMarker from 'assets/images/marker.svg'
import constants from 'utils/constants'
import getBounds from 'utils/getBounds'
import useResponsive from 'hooks/useResponsive'
import { IApplicationState } from 'components/store/types'
import { getMapActiveMarker, getRegionCliniques } from 'components/core/state/selectors'
import { useTranslation } from 'react-i18next'

type LatLngLiteral = google.maps.LatLngLiteral

const mapState = (state: IApplicationState) => ({
  mapActiveMarker: getMapActiveMarker(state),
  cliniques: getRegionCliniques(state),
})
const mapDispatch = (dispatch: AppDispatch) => ({
  updateClinique: (cliniqueId: number, setSearchParams: ISetSearchParams) => {
    dispatch(updateCurrentClinique(cliniqueId, setSearchParams))
  },
  updateMapMarker: (markerId: number) => dispatch(updateMapActiveMarker(markerId)),
  updateStep: (step: number, setSearchParams: ISetSearchParams) => {
    dispatch(updateCurrentStep(step, setSearchParams))
  },
})
const connector = connect(mapState, mapDispatch)

const GoogleMapWrapper = ({
  cliniques,
  mapActiveMarker,
  updateClinique,
  updateMapMarker,
  updateStep,
}: ConnectedProps<typeof connector>) => {
  const [, setSearchParams] = useSearchParams()
  const [center, setCenter] = useState<LatLngLiteral>({ lat: 46, lng: 6 })
  const [zoom, setZoom] = useState<number>(7)
  const { compareTag } = useResponsive()
  const { t } = useTranslation()
  const handleClick = (cliniqueId: number) => {
    updateClinique(cliniqueId, setSearchParams)
    updateStep(3, setSearchParams)
  }
  const handleDomReady = (cliniqueId) => {
    const takeAppointmentButton = document.querySelector('.gmap-appointment')
    if (takeAppointmentButton) {
      takeAppointmentButton.addEventListener('click', () => handleClick(cliniqueId))
    }
  }
  const onMarkerClick = (cliniqueId) => updateMapMarker(cliniqueId)

  useEffect(() => {
    if (!cliniques.length) {
      setZoom(13)
    } else if (cliniques.length === 1) {
      const { acf: { marker: { lat, lng } } } = cliniques[0]
      setCenter({ lat, lng })
      setZoom(13)
    } else {
      const {
        minLat,
        maxLat,
        minLng,
        maxLng,
      } = getBounds(cliniques.map((clinique) => clinique.acf.marker).filter(Boolean))
      const { height, smHeight } = constants.map
      const mapHeight = compareTag('md') ? height : smHeight
      const { center: boundsCenter, zoom: boundsZoom } = fitBounds({
        ne: {
          lat: maxLat,
          lng: maxLng,
        },
        sw: {
          lat: minLat,
          lng: minLng,
        },
      }, {
        height: mapHeight,
        width: mapHeight,
      })
      setCenter(boundsCenter)
      setZoom(boundsZoom)
    }
  }, [cliniques])

  return (
    <Wrapper apiKey="AIzaSyAxcG1M6Ang_PXJKZZ_-glr3HeFYVBForw">
      <GoogleMap mapId="baf8ea9d3fed2bdb" zoom={zoom} center={center}>
        {cliniques.map((clinique: IClinique) => (
          <GoogleMapMarker
            active={mapActiveMarker === clinique.id}
            key={clinique.id}
            position={{ lat: clinique.acf.marker.lat, lng: clinique.acf.marker.lng }}
            icon={mapActiveMarker === clinique.id ? DActiveMarker : DMarker}
            infoWindowContent={renderToString(
              <GoogleMapInfoWindow
                clinique={clinique}
                mobile={!compareTag('md')}
                t={t}
              />
            )}
            onClick={() => onMarkerClick(clinique.id)}
            onDomReady={() => handleDomReady(clinique.id)}
          />
        ))}
      </GoogleMap>
    </Wrapper>
  )
}
export default connector(GoogleMapWrapper)
